var React = require('react');
var { createElement: h, useState, useEffect, useRef } = React;
var { streamChatWorkflowPlan } = require('../lib/workflowStorage');
var { formatText, stripStreamingXml, parseAiMessage, isPrematureStop, hasQuestionPartAfter, QuestionTabs, selectionText } = require('../lib/plannerChatFormat');

// The chat surface inside the "AI Flow Builder" balloon for custom/agent nodes.
// It reuses the planner's discovery stream (singleNode mode) so a single node
// gets the same probing + Q&A treatment as a full workflow. When the model
// emits a <node> spec the server returns it on the `done` event as
// `singleNode`; we hand that to onNodeReady(spec) which runs codegen + drops
// the node. Until then this renders streaming thinking, probe results, and any
// clarifying questions as option chips.
//
// Props:
//   initialPrompt      — the user's first request (sent automatically on mount)
//   agentMode          — user's Agent-Node toggle (passed as a hint)
//   modelTier          — 'fast' | 'balanced' (single nodes never use Opus)
//   responseFields     — user-entered agent response fields (hint)
//   singleNodeContext  — source-node snapshot for the stream
//   onNodeReady(spec)  — called with { prompt, agentMode, responseFields }
//   onClose()          — dismiss the balloon
function AiFlowChatPanel(props) {
  var onNodeReady = props.onNodeReady || function() {};
  var onClose = props.onClose || function() {};

  var [messages, setMessages] = useState([]);
  var [loading, setLoading] = useState(false);
  var [error, setError] = useState('');
  var [input, setInput] = useState('');
  var [streamingText, setStreamingText] = useState('');
  var [thinkingStatus, setThinkingStatus] = useState('');
  var [probeEvents, setProbeEvents] = useState([]);
  // Per-question selection state (mirrors the planner's option-chip flow).
  var [selections, setSelections] = useState({});
  var [otherOpen, setOtherOpen] = useState(null);
  var [otherText, setOtherText] = useState('');

  var abortRef = useRef(null);
  var scrollRef = useRef(null);
  var chatSessionIdRef = useRef(Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8));

  // Auto-continue guard (mirrors WorkflowPlannerModal): a single-node turn that
  // signals <ready/> but emits no <node> leaves nothing to render and no node to
  // build — so the balloon would dead-stall and the future-connector dangles.
  // On such a stall we auto-send "continue" so the model finishes the <node>.
  var [pendingAutoContinue, setPendingAutoContinue] = useState(false);
  var autoContinueCountRef = useRef(0);
  var MAX_AUTO_CONTINUE = 3;

  // Single-node discovery ALWAYS runs on Sonnet (planner 'balance' tier) — no
  // model choice in the balloon, never Haiku/Opus.
  var plannerMode = 'balance';

  // Auto-scroll the chat to the latest content. Set scrollTop on the inner
  // container ONLY — never scrollIntoView, which also scrolls outer ancestors
  // (the canvas) and shoves the absolutely-positioned balloon off-screen.
  // Defer to the next frame so the new turn (incl. the fixed-height coding-lines
  // block) is laid out before we measure scrollHeight, otherwise we stop short.
  useEffect(function() {
    var id = requestAnimationFrame(function() {
      var el = scrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    });
    return function() { cancelAnimationFrame(id); };
  }, [messages, streamingText, probeEvents, thinkingStatus, loading]);

  // Kick off the conversation with the user's prompt (+ a node-type hint).
  // Runs once on mount; the cleanup aborts the stream so a remount (e.g. dev
  // StrictMode double-invoke) cleanly restarts rather than stalling.
  useEffect(function() {
    var hint;
    if (props.agentMode) {
      var fields = (props.responseFields || []).filter(function(f) { return String(f || '').trim(); });
      hint = '\n\n(Build this as an AI agent node that uses chat()'
        + (fields.length ? ', returning fields: ' + fields.join(', ') : '') + '.)';
    } else {
      hint = '\n\n(Build this as a deterministic custom code node.)';
    }
    doSend(String(props.initialPrompt || '').trim() + hint, []);
    return function() { if (abortRef.current) { try { abortRef.current(); } catch (e) {} } };
    // eslint-disable-next-line
  }, []);

  function clearSelections() {
    setSelections({});
    setOtherOpen(null);
    setOtherText('');
  }

  function doSend(text, history, isAuto) {
    if (!text || loading) return;
    // A real user send resets the auto-continue budget; our own injected
    // "continue" (isAuto) must not, or the cap would never bite.
    if (!isAuto) autoContinueCountRef.current = 0;
    var base = Array.isArray(history) ? history : messages;
    var userMessage = { role: 'user', content: text };
    var next = base.concat([userMessage]);
    setMessages(next);
    setInput('');
    setLoading(true);
    setError('');
    setStreamingText('');
    setThinkingStatus('');
    setProbeEvents([]);
    clearSelections();

    var apiMessages = next.map(function(m) { return { role: m.role, content: m.content }; });

    if (abortRef.current) { try { abortRef.current(); } catch (e) {} abortRef.current = null; }

    abortRef.current = streamChatWorkflowPlan(apiMessages, null, {
      onEvent: function(evt) {
        if (evt.type === 'thinking') {
          setThinkingStatus(evt.text || 'Thinking...');
        } else if (evt.type === 'chunk_start') {
          setThinkingStatus('');
          setStreamingText('');
        } else if (evt.type === 'chunk') {
          setStreamingText(evt.fullText || '');
        } else if (evt.type === 'probes_start') {
          setStreamingText('');
          setProbeEvents([]);
        } else if (evt.type === 'probe_running') {
          setProbeEvents(function(prev) {
            return prev.concat([{ label: evt.label, status: 'running', probeType: evt.probeType }]);
          });
        } else if (evt.type === 'probe_result') {
          setProbeEvents(function(prev) {
            return prev.map(function(pe) {
              if (pe.label === evt.label && pe.status === 'running') {
                return { label: evt.label, status: evt.status, probeType: evt.probeType, output: evt.output };
              }
              return pe;
            });
          });
        } else if (evt.type === 'done') {
          abortRef.current = null;
          setLoading(false);
          setStreamingText('');
          setThinkingStatus('');
          setProbeEvents([]);
          // Node spec ready → hand off to codegen + drop.
          if (evt.singleNode && evt.singleNode.prompt) {
            onNodeReady(evt.singleNode);
            return;
          }
          // Otherwise this was a question / mid-conversation turn — show it.
          var content = evt.rawMessage || evt.message || '';
          var display = evt.message || content;
          var parsed = parseAiMessage(display);
          // A turn that's only a <ready/> control token has no visible content —
          // don't push an empty assistant bubble (that's the raw "<ready/>" leak).
          var hasVisible = parsed.parts.some(function(p) {
            return p.type !== 'text' || (p.text && p.text.trim());
          });
          if (content && hasVisible) {
            setMessages(function(prev) {
              return prev.concat([{ role: 'assistant', content: content, displayContent: display }]);
            });
          }
          // Stall guard: no node spec AND nothing to render/click (a bare <ready/>
          // that never emitted the <node>, or a trailed-off turn). Auto-send
          // "continue" so the model finishes the node — capped so a stuck model
          // can't loop forever. isPrematureStop covers the trailed-off case; the
          // empty-after-strip case covers a bare <ready/> with no node.
          var awaitsUser = parsed.parts.some(function(p) {
            return p.type === 'question' || p.type === 'input';
          });
          var stalled = !awaitsUser && (!hasVisible || isPrematureStop(evt.rawMessage || ''));
          if (stalled && autoContinueCountRef.current < MAX_AUTO_CONTINUE) {
            autoContinueCountRef.current += 1;
            // Defer to an effect so the resend runs from a fresh render whose
            // `messages` already include any pushed reply (stale-state safe).
            setPendingAutoContinue(true);
          }
        } else if (evt.type === 'error') {
          abortRef.current = null;
          setLoading(false);
          setStreamingText('');
          setThinkingStatus('');
          setProbeEvents([]);
          setError(evt.message || 'Something went wrong. Please try again.');
        }
      },
      onError: function(err) {
        abortRef.current = null;
        setLoading(false);
        setStreamingText('');
        setThinkingStatus('');
        setProbeEvents([]);
        setError(err && err.message ? err.message : 'Something went wrong. Please try again.');
      }
    }, { mode: plannerMode, chatSessionId: chatSessionIdRef.current, singleNode: true, singleNodeContext: props.singleNodeContext || null });
  }

  function stopStream() {
    if (abortRef.current) { try { abortRef.current(); } catch (e) {} abortRef.current = null; }
    setLoading(false);
    setStreamingText('');
    setThinkingStatus('');
    setProbeEvents([]);
    // User explicitly stopped — abandon any queued auto-continue.
    setPendingAutoContinue(false);
  }

  // Fire the queued auto-continue once the stream is idle. Runs from a fresh
  // render so doSend sees up-to-date `messages` (same idiom as the planner).
  useEffect(function() {
    if (pendingAutoContinue && !loading) {
      setPendingAutoContinue(false);
      doSend('continue', null, true);
    }
    // eslint-disable-next-line
  }, [pendingAutoContinue, loading]);

  function submitSelections(questions) {
    var parts = [];
    for (var i = 0; i < questions.length; i++) {
      var q = questions[i];
      // Multi-select answers arrive as an array — selectionText joins them.
      var ans = selectionText(selections[i]);
      if (ans) {
        parts.push(q.bareReply ? ans : (q.question + ': ' + ans));
      }
    }
    if (parts.length === 0) return;
    doSend(parts.join('\n'), messages);
  }

  function onInputKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      var t = input.trim();
      if (t) doSend(t, messages);
    }
  }

  // ── Render one AI message (text + interactive question chips) ───────────────
  function renderAiMessage(msg, idx, isLast) {
    var parsed = parseAiMessage(msg.displayContent || msg.content);
    var questions = parsed.parts.filter(function(p) { return p.type === 'question' || p.type === 'input'; });
    var interactive = isLast && questions.length > 0 && !loading;
    var qIdx = 0;
    var els = [];

    // Skip / Next / Submit footer props — rendered by QuestionTabs (with the
    // group holding the LAST question) so Submit only shows on the final tab.
    var footerProps = null;
    if (interactive) {
      var answered = Object.keys(selections).filter(function(k) { return selections[k] && String(selections[k]).trim(); }).length;
      footerProps = {
        answered: answered,
        total: questions.length,
        onSkip: function() { doSend('Skip — proceed with your best judgment.', messages); },
        onSubmit: function() { submitSelections(questions); }
      };
    }

    // Consecutive question parts collapse into ONE tabbed control (QuestionTabs)
    // instead of a long stacked scroll; a run of one renders as a plain block.
    var pendingQuestions = [];
    function flushQuestions() {
      if (pendingQuestions.length === 0) return;
      els.push(h(QuestionTabs, {
        key: 'qgrp-' + pendingQuestions[0].key,
        items: pendingQuestions,
        // Only the run ending at the message's last question carries the footer.
        footer: qIdx === questions.length ? footerProps : null
      }));
      pendingQuestions = [];
    }

    parsed.parts.forEach(function(part, pIdx) {
      if (part.type === 'text') {
        // Connector prose between questions ("And one more:") renders, but
        // must NOT flush the run — flushing here split the tabbed control
        // into stacked single-question blocks with no tab bar.
        if (!hasQuestionPartAfter(parsed.parts, pIdx)) flushQuestions();
        els.push(h('div', { key: 't-' + pIdx, className: 'ai-flow-chat-text' }, formatText(part.text)));
        return;
      }
      if (part.type === 'question' || part.type === 'input') {
        // Shared card-style Q&A control — same component the planner modal uses.
        var myIdx = qIdx++;
        pendingQuestions.push({ key: 'q-' + pIdx, props: {
          part: part,
          interactive: interactive,
          selected: selections[myIdx] || '',
          onSelect: (function(qi) {
            return function(value) {
              var c = Object.assign({}, selections);
              if (value && String(value).trim()) c[qi] = value; else delete c[qi];
              setSelections(c);
            };
          })(myIdx),
          otherOpen: otherOpen === myIdx,
          otherText: otherOpen === myIdx ? otherText : '',
          onOtherToggle: (function(qi) {
            return function(open, seed) {
              setOtherOpen(open ? qi : null);
              setOtherText(open ? (seed || '') : '');
            };
          })(myIdx),
          onOtherText: setOtherText
        } });
      }
    });
    flushQuestions();

    return h('div', { key: idx, className: 'ai-flow-chat-msg ai-flow-chat-msg-ai' }, els);
  }

  var cleanStream = stripStreamingXml(streamingText);

  return h('div', { className: 'ai-flow-chat' },
    h('div', { className: 'ai-flow-chat-scroll', ref: scrollRef },
      messages.map(function(msg, idx) {
        if (msg.role === 'user') {
          return h('div', { key: idx, className: 'ai-flow-chat-msg ai-flow-chat-msg-user' }, msg.content);
        }
        return renderAiMessage(msg, idx, idx === messages.length - 1);
      }),

      loading && cleanStream
        ? h('div', { className: 'ai-flow-chat-msg ai-flow-chat-msg-ai ai-flow-chat-streaming' },
            formatText(cleanStream), h('span', { className: 'ai-flow-chat-cursor' }))
        : null,

      loading && probeEvents.length > 0
        ? h('div', { className: 'ai-flow-chat-probes' },
            probeEvents.map(function(pe, i) {
              var icon = pe.status === 'running' ? '⏳' : pe.status === 'ok' ? '✅' : '❌';
              return h('div', { key: i, className: 'planner-probe-event planner-probe-' + pe.status },
                h('span', { className: 'planner-probe-event-icon' }, icon),
                h('span', { className: 'planner-probe-event-label' }, pe.label),
                pe.output && pe.status !== 'running'
                  ? h('div', { className: 'planner-probe-event-output' }, pe.output)
                  : null
              );
            }))
        : null,

      loading && !cleanStream && probeEvents.length === 0
        ? h('div', { className: 'ai-flow-popover-coding ai-flow-chat-coding' },
            h('div', { className: 'ai-flow-popover-coding-status' },
              h('span', { className: 'ai-flow-popover-coding-spinner' }),
              h('span', { className: 'ai-flow-popover-coding-label' }, thinkingStatus || 'Working')
            )
          )
        : null,

      error ? h('div', { className: 'ai-flow-popover-error' }, error) : null
    ),

    h('div', { className: 'ai-flow-chat-input-row' },
      h('textarea', {
        className: 'ai-flow-popover-input ai-flow-chat-input',
        rows: 1,
        placeholder: 'Type a reply...',
        value: input,
        disabled: loading,
        onChange: function(e) { setInput(e.target.value); },
        onKeyDown: onInputKeyDown,
        spellCheck: false
      }),
      loading
        ? h('button', { className: 'ai-flow-chat-send ai-flow-chat-stop', onClick: stopStream, title: 'Stop' },
            h('span', { className: 'ai-flow-chat-stop-square' }))
        : h('button', {
            className: 'ai-flow-chat-send',
            disabled: !input.trim(),
            onClick: function() { var t = input.trim(); if (t) doSend(t, messages); },
            title: 'Send'
          }, h('svg', { width: 15, height: 15, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2 },
            h('line', { x1: 22, y1: 2, x2: 11, y2: 13 }),
            h('polygon', { points: '22 2 15 22 11 13 2 9 22 2' })
          ))
    )
  );
}

module.exports = AiFlowChatPanel;
